import React, { useState, useContext, useRef } from "react";
import { Link, withRouter } from "react-router-dom";
import DataTable from "core/ui/data-table";
import FileUploader from "core/ui/forms/file-uploader";
import SessionContext from "SessionContext";
import FormatDateTime from "core/utils/FormatDateTime";
import { useHistory } from "react-router-dom";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from 'reactstrap';
import ConfirmSuccess from "../core/ui/confirm-success";
import AuthTokenContext from "AuthTokenContext";
import { handleDownloadSource } from "../core/utils/download-source-file.js"


const TableActions = (upload) => {
  const session = useContext(SessionContext);
  const tokens = useContext(AuthTokenContext);
  const access = session.appState.userCan;
  const submit_destination = "/process-location-upload/" + upload.id;
  const details_destination = "/location-upload-details/" + upload.id;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const toggleDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  }

  const onCancelDelete = () => {
    toggleDeleteModal();
  }

  const onFinalDeleteUpload = () => {
    session.command(`upload/delete_location_upload?id=${upload.id}`)
      .then(() => {
        toggleDeleteModal();
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          window.location.reload();
        }, 2000);
      })
      .catch((err) => {
        console.log("Delete Failed", JSON.stringify(err));
      })
  }

  return (<>
    <div className="actions-wrapper">
      <div>
        {upload.status === "Uploaded" && (
          <Button>
            <Link to={ {pathname: submit_destination, state: {refer: "location-uploads"}} }>
              <i className="fa fa-map" /> Submit
            </Link>
          </Button>
        )}
        <Button>
          <Link to={{ pathname: details_destination }}>
            <i className="bi bi-eye" /> Details
          </Link>
        </Button>
        {access('location_delete') && <>
          <Button className="mx-2" onClick={toggleDeleteModal}>
            <i className="bi bi-trash" /> Delete
          </Button>
        </>}
      </div>
      

      <Modal centered backdrop
        isOpen={showDeleteModal}
      >
        <ModalHeader>
          Delete Location Upload
        </ModalHeader>
        <ModalBody>
          <Alert color="light" className="d-flex">
            <i className="bi bi-exclamation-octagon"></i>
            <div>
            Selecting <b>delete</b> will permanently remove all instances from AudienceMaker and cannot be undone. Are you sure you want to continue?
            </div>
          </Alert>
          <Alert color="light" className="">
            <p>
              <span className="header-modal-text">Location Upload File Name</span>
              <span className="d-block ms-3">{upload.filename}</span>
            </p>
            <p>
              <span className="header-modal-text">Description</span>
              <span className="d-block ms-3">{upload.description}</span>
            </p>
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button className="promote" onClick={onCancelDelete}>Cancel</Button>
          <Button onClick={onFinalDeleteUpload}>Delete</Button>
        </ModalFooter>
      </Modal>
      <ConfirmSuccess show={showSuccess} label="Location Upload deleted successfully." />
    </div>
    <div className="actions-wrapper">
      <ul className="action-menu">
        <li>
          {access('administration') && (
            <Button color="link" size="sm" className="mx-1" onClick={() => handleDownloadSource(upload, tokens, session)}>
              <i className="bi bi-download" /> Download Source
            </Button>
          )}
        </li>
      </ul>
    </div>
  </>)
};

function timeout(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const LocationUploads = (props) => {
  const context = useContext(SessionContext);
  const abortFlagRef = useRef(false);
  const fileUploadCompleteRef = useRef(false);

  const table_columns = [
    { header: "Filename", key: "filename" },
    { header: "Description", key: "description" },
    {
      header: "Uploaded",
      key: "ingress_date",
      render: (value) => <FormatDateTime date={value.ingress_date} />,
    },
    { header: "Uploaded By", key: "uploaded_by" },
    {
      header: "Status",
      key: "status",
      render: (value) => onClickUploadStatus(value),
    },
  ];

  function onClickUploadStatus(value) {
    const orderId = { id: value.id };
    const pathBack = { back: window.location.pathname };
    const info = {...orderId, ...pathBack}
    return (
      <div className="status-link">
        <i className="fa fa-circle-info i-info" />{" "}
        <Link to={{ pathname: "/upload-status", state: info }}>
          {value.status}
        </Link>
      </div>
    );
  }

  function onFileUploadStart(file) {
    fileUploadCompleteRef.current = false;
    const reader = new FileReader();
    reader.onload = async () => {
      let upload;
      let failed = false;
      while (!fileUploadCompleteRef.current) {
        await timeout(500);
      }
      if (!abortFlagRef.current) {
        upload = await context
          .query("uploads/get", { id: file.id })
          .catch((e) => {
            console.log(e);
            failed = true;
          });
        if (!failed) {
          if (
            upload.content_descriptor &&
            upload.content_descriptor.columns
          ) {
            props.history.push(`/process-location-upload/${file.id}`);
          }
        }
      } else {
        failed = true;
        //reset flag for next upload
        abortFlagRef.current = false;
      }
    };
    reader.readAsText(file.slice(0, 4096 * 4));
  }

  function abortFlag(value) {
    abortFlagRef.current = value;
  }

  function onFileUploadComplete(value) {
    fileUploadCompleteRef.current = value;
  }

  return (
    <div className="page uploads-page">
      <div className="list-header sticky-page-header">
        <GoBackButton />
        <h1>Upload a list of locations</h1>
        <i className="spacer" />
      </div>

      <FileUploader
        accept=".csv"
        multiple={false}
        onFileUploadStart={onFileUploadStart}
        abortFlag={abortFlag}
        onFileUploadComplete={onFileUploadComplete}
        isLocationUpload={true}
      />
      <div>
        <h1>Past Location List Uploads</h1>
      </div>
      <div className="past-uploads">
        <DataTable
          query="uploads/listLocations"
          cols={table_columns}
          actions={TableActions}
          empty="No Uploads Found"
        />
      </div>
    </div>
  );
};
export default withRouter(LocationUploads);
/**
 * use hooks for the back button
 */
function GoBackButton() {
  const history = useHistory();
  const goBack = () => {
    history.push("/locations");
  };
  return (
    <button type="button" className="new-button" onClick={goBack}>
      <i className="bi bi-chevron-left"></i>{" "} Locations
    </button>
  );
}
